import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty, isPlainObject } from 'remeda'
import { useExchangeRates } from '@components/ExchangeRatesProvider'
import { QuickAdd } from '@components/QuickAdd/QuickAdd'
import { WishlistCTA } from '@components/WishlistCTA/WishlistCTA'
import { dataTestIds } from '@velasca/constants'
import { PlusIconSmall } from '@velasca/icons/plus-small'
import { ConditionalLink } from '@velasca/ui/conditional-link'
import { HorizontalCardDescription } from '@velasca/ui/horizontal-card-description'
import { HorizontalCardPrice } from '@velasca/ui/horizontal-card-price'
import { HorizontalCardTitle } from '@velasca/ui/horizontal-card-title'
import { PriceRow } from '@velasca/ui/price-row'
import { SansText } from '@velasca/ui/sans-text'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { setQueryParams } from '@velasca/utils/set-query-params'
import { QuickAddSimpleProductButton } from './QuickAddSimpleProductButton'
import {
  Background,
  Button,
  Card,
  Details,
  ImageWrapper,
  ProductImage,
  Separator,
  Spacer,
} from './SuggestedHorizontalProduct.styles'
import type { SuggestedHorizontalProductProps } from './SuggestedHorizontalProduct.types'

export const SuggestedHorizontalProduct = ({
  title,
  productType,
  variant,
  isGiftCard,
  sizes,
  shouldOpenCartDrawerOnAddToCart,
  isQuickAdd = true,
  onAddToCart,
  onQuickAdd,
}: SuggestedHorizontalProductProps): JSX.Element => {
  const { t: globalT } = useTranslation('global')
  const { t } = useTranslation('cartDrawer')
  const [isQuickAddPopupOpen, setIsQuickAddPopupOpen] = useState<boolean>(false)

  const { convert } = useExchangeRates()

  const price = useMemo(() => {
    const minPrice = isPlainObject(variant.price) ? variant.price?.min * 100 : 0
    const maxPrice = isPlainObject(variant.price) ? variant.price?.max * 100 : 0

    if (minPrice !== maxPrice) {
      return `${convert(minPrice)} - ${convert(maxPrice)}`
    }

    return convert(maxPrice)
  }, [variant, convert])

  const compareAtPrice = useMemo(() => {
    const minPrice = isPlainObject(variant?.compareAtPrice)
      ? variant?.compareAtPrice?.min * 100
      : 0
    const maxPrice = isPlainObject(variant?.compareAtPrice)
      ? variant?.compareAtPrice?.max * 100
      : 0

    if (minPrice !== maxPrice) {
      return `${convert(minPrice)} - ${convert(maxPrice)}`
    }

    return convert(maxPrice)
  }, [variant, convert])

  const isPriceDiscounted = useMemo(() => {
    const nomalizedCompareAtPrice = isPlainObject(variant?.compareAtPrice)
      ? variant?.compareAtPrice?.min
      : 0
    const nomalizedPrice = isPlainObject(variant?.price)
      ? variant?.price?.max
      : 0

    return nomalizedCompareAtPrice > nomalizedPrice
  }, [variant])

  const {
    handle,
    description,
    img,
    name,
    material_desc_tag,
    color_desc_tag,
    sole_tag,
    id,
  } = variant

  return (
    <>
      <Background>
        <Card>
          <WishlistCTA
            layout="tile-inverted"
            productData={{
              handle: handle ?? '',
              productId: String(id ?? ''),
            }}
          />
          <a href={`/products/${handle ?? ''}`}>
            <ImageWrapper>
              {img?.front && (
                <ProductImage
                  lazyload
                  lqip
                  showDefault
                  sources={buildIntrinsicWidthSources({ src: img.front })}
                  draggable={false}
                  transitionDuration={50}
                  imgSrc={setQueryParams(img.front, [
                    { name: 'width', value: '30' },
                  ])}
                  alt={t('cart_drawer_product_alt_template', {
                    color: description?.bold,
                    material: description?.thin,
                    productTitle: title,
                    productType,
                  })}
                />
              )}
            </ImageWrapper>
          </a>

          <Details>
            <HorizontalCardDescription className="product-type">
              {productType}
            </HorizontalCardDescription>
            <ConditionalLink
              condition={Boolean(handle)}
              href={`/products/${handle}`}
            >
              <HorizontalCardTitle>{title}</HorizontalCardTitle>
            </ConditionalLink>
            <Spacer>
              <HorizontalCardDescription className="bold">
                {!isEmpty(description?.bold ?? '')
                  ? description?.bold
                  : !isEmpty(description?.composition)
                    ? description?.composition
                    : material_desc_tag?.value}
              </HorizontalCardDescription>

              <HorizontalCardDescription>
                {!isEmpty(description?.thin ?? '')
                  ? description?.thin
                  : !isEmpty(description?.color)
                    ? description?.color
                    : color_desc_tag?.value}
              </HorizontalCardDescription>

              {!isEmpty(sole_tag?.value) && (
                <HorizontalCardDescription>
                  {sole_tag?.value}
                </HorizontalCardDescription>
              )}
            </Spacer>
            <PriceRow>
              {isPriceDiscounted && (
                <HorizontalCardPrice
                  strikethrough
                  color="gray"
                  style={{
                    fontSize: '1.4rem',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: compareAtPrice,
                  }}
                />
              )}
              {price && (
                <HorizontalCardPrice
                  fontWeight="500"
                  dangerouslySetInnerHTML={{
                    __html: price,
                  }}
                />
              )}
            </PriceRow>
          </Details>
        </Card>

        {isQuickAdd && (
          <>
            <Separator />
            {sizes?.length === 1 ? (
              <QuickAddSimpleProductButton
                size={sizes[0]}
                handle={handle}
                shouldOpenCartDrawerOnAddToCart={
                  shouldOpenCartDrawerOnAddToCart
                }
                onQuickAdd={onQuickAdd}
              />
            ) : (
              <Button
                data-testid={dataTestIds.QUICK_ADD_TO_CART_BUTTON}
                onClick={() => {
                  setIsQuickAddPopupOpen(true)
                  onQuickAdd && onQuickAdd(handle)
                }}
              >
                <SansText uppercase as="span" size="sm" fontWeight={'500'}>
                  {globalT('products_commons_choose_size_selection')}
                </SansText>
                <PlusIconSmall color="dark" width="18" height="18" />
              </Button>
            )}
          </>
        )}
      </Background>

      {isQuickAdd && (
        <QuickAdd
          isPriceDiscounted={isPriceDiscounted}
          compareAtPrice={compareAtPrice}
          title={title || ''}
          price={price}
          productType={productType}
          description={description}
          isGiftCard={isGiftCard}
          sizes={sizes}
          handle={handle}
          isOpen={isQuickAddPopupOpen}
          shouldOpenCartDrawerOnAddToCart={shouldOpenCartDrawerOnAddToCart}
          image={{
            src: img?.front ?? '',
            alt: name ?? '',
          }}
          onAddToCart={onAddToCart}
          onClose={() => {
            setIsQuickAddPopupOpen(false)
          }}
        />
      )}
    </>
  )
}
